import { apiClient } from './api'
import { Customer } from '@/types'

export interface CustomerListParams {
  page?: number
  limit?: number
  search?: string
  kyc_verified?: boolean
  country_of_residence?: string
  sort?: string
  ordering?: 'asc' | 'desc'
}

export interface CustomerListResponse {
  success: boolean
  message: string
  data: {
    results: Customer[]
    count: number
    next: string | null
    previous: string | null
  }
}

export interface CustomerStats {
  total_customers: number
  verified_customers: number
  pending_customers: number
  new_this_month: number
}

class CustomerService {
  // Get customers with pagination and filtering
  async getCustomers(params?: CustomerListParams): Promise<CustomerListResponse> {
    const queryParams = new URLSearchParams()

    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.search) queryParams.append('search', params.search)
    if (params?.kyc_verified !== undefined)
      queryParams.append('kyc_verified', params.kyc_verified.toString())
    if (params?.country_of_residence)
      queryParams.append('country_of_residence', params.country_of_residence)
    if (params?.sort) queryParams.append('sort', params.sort)
    if (params?.ordering) queryParams.append('ordering', params.ordering)

    const queryString = queryParams.toString()
    const url = queryString ? `/customers/?${queryString}` : '/customers/'

    return apiClient.get<CustomerListResponse>(url)
  }

  // Get customer by ID
  async getCustomer(customerId: number): Promise<Customer> {
    return apiClient.get<Customer>(`/customers/${customerId}/`)
  }

  // Get customer statistics
  async getCustomerStats(): Promise<CustomerStats> {
    return apiClient.get<CustomerStats>('/customers/stats/')
  }

  // Update customer
  async updateCustomer(customerId: number, data: Partial<Customer>): Promise<Customer> {
    return apiClient.patch<Customer>(`/customers/${customerId}/`, data)
  }

  // Delete customer
  async deleteCustomer(customerId: number): Promise<{ success: boolean; message: string }> {
    return apiClient.delete<{ success: boolean; message: string }>(`/customers/${customerId}/`)
  }

  // Verify customer KYC
  async verifyCustomerKYC(customerId: number): Promise<Customer> {
    return apiClient.post<Customer>(`/customers/${customerId}/verify-kyc/`)
  }

  // Reject customer KYC
  async rejectCustomerKYC(customerId: number, reason?: string): Promise<Customer> {
    return apiClient.post<Customer>(`/customers/${customerId}/reject-kyc/`, { reason })
  }

  // Get customer transactions
  async getCustomerTransactions(
    customerId: number,
    params?: {
      page?: number
      limit?: number
      status?: string
    }
  ): Promise<{
    success: boolean
    message: string
    data: {
      results: any[]
      count: number
      next: string | null
      previous: string | null
    }
  }> {
    const queryParams = new URLSearchParams()

    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.status) queryParams.append('status', params.status)

    const queryString = queryParams.toString()
    const url = queryString
      ? `/customers/${customerId}/transactions/?${queryString}`
      : `/customers/${customerId}/transactions/`

    return apiClient.get<{
      success: boolean
      message: string
      data: {
        results: any[]
        count: number
        next: string | null
        previous: string | null
      }
    }>(url)
  }

  // Export customers
  async exportCustomers(params?: {
    format?: 'csv' | 'xlsx'
    kyc_verified?: boolean
    country_of_residence?: string
  }): Promise<Blob> {
    const queryParams = new URLSearchParams()

    if (params?.format) queryParams.append('format', params.format)
    if (params?.kyc_verified !== undefined)
      queryParams.append('kyc_verified', params.kyc_verified.toString())
    if (params?.country_of_residence)
      queryParams.append('country_of_residence', params.country_of_residence)

    const queryString = queryParams.toString()
    const url = queryString ? `/customers/export/?${queryString}` : '/customers/export/'

    return apiClient.get<Blob>(url, {
      responseType: 'blob',
    })
  }

  // Handle API errors
  handleError(error: any): string {
    if (error.response?.data?.message) {
      return error.response.data.message
    }
    if (error.response?.data?.errors) {
      return Array.isArray(error.response.data.errors)
        ? error.response.data.errors.join(', ')
        : error.response.data.errors
    }
    if (error.message) {
      return error.message
    }
    return 'An unexpected error occurred'
  }
}

export const customerService = new CustomerService()
